import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './Auth.css';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '', confirm_password: '' });
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (e.target.name === 'phone' && !e.target.value) setSmsOptIn(false);
    setFieldErrors(fe => ({ ...fe, [e.target.name]: '' }));
    setError('');
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'Required';
    if (!form.last_name.trim()) errs.last_name = 'Required';
    if (!form.email.trim()) errs.email = 'Required';
    if (form.password.length < 8) errs.password = 'Minimum 8 characters';
    else if (!/[A-Z]/.test(form.password)) errs.password = 'Must contain at least one uppercase letter';
    else if (!/[0-9]/.test(form.password)) errs.password = 'Must contain at least one number';
    if (form.password !== form.confirm_password) errs.confirm_password = 'Passwords do not match';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setLoading(true);
    setError('');
    try {
      const result = await register({ first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone, password: form.password, role: 'parent', sms_notifications: form.phone ? smsOptIn : false });
      navigate(result.user?.email_verified === false ? '/pending-verification' : '/');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-branding">
        <div className="auth-branding-inner">
          <div className="auth-logo"><img src="/wf-logo.png" alt="Wow Factor Baseball" style={{width:"192px",height:"192px",objectFit:"contain"}} /></div>
          <h1 className="auth-brand-name">WOW FACTOR</h1>
          <p className="auth-brand-sub">Baseball Training & Development</p>
          <div className="auth-brand-divider" />
          <p className="auth-brand-quote">"Elevating the game,<br />one session at a time."</p>
        </div>
      </div>
      <div className="auth-form-panel">
        <div className="auth-form-container">
          <div className="auth-form-header">
            <h2>Create account</h2>
            <p>Register to book training sessions</p>
          </div>
          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="auth-error-banner"><span>⚠</span> {error}</div>}
            <div className="auth-name-row">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input name="first_name" type="text" className={`form-input ${fieldErrors.first_name ? 'error' : ''}`} placeholder="John" value={form.first_name} onChange={handleChange} />
                {fieldErrors.first_name && <span className="error-text">{fieldErrors.first_name}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input name="last_name" type="text" className={`form-input ${fieldErrors.last_name ? 'error' : ''}`} placeholder="Smith" value={form.last_name} onChange={handleChange} />
                {fieldErrors.last_name && <span className="error-text">{fieldErrors.last_name}</span>}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input name="email" type="email" className={`form-input ${fieldErrors.email ? 'error' : ''}`} placeholder="you@example.com" value={form.email} onChange={handleChange} />
              {fieldErrors.email && <span className="error-text">{fieldErrors.email}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Phone <span className="auth-optional">(optional)</span></label>
              <input name="phone" type="tel" className="form-input" placeholder="+1 (555) 000-0000" value={form.phone} onChange={handleChange} />
            </div>
            {form.phone && (
              <div className="form-group" style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '14px 16px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', margin: 0 }}>
                  <input type="checkbox" checked={smsOptIn} onChange={e => setSmsOptIn(e.target.checked)} style={{ marginTop: '3px', flexShrink: 0, accentColor: '#ffffff' }} />
                  <span style={{ fontSize: '13px', color: '#a0a0a0', lineHeight: '1.5' }}>
                    I agree to receive SMS text message notifications for session updates, reminders, and alerts from Wow Factor Baseball. Message &amp; data rates may apply. You may opt out at any time from your profile settings or reply STOP to any message.{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>Privacy Policy</a>
                  </span>
                </label>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Password</label>
              <input name="password" type="password" className={`form-input ${fieldErrors.password ? 'error' : ''}`} placeholder="Min 8 chars, 1 uppercase, 1 number" value={form.password} onChange={handleChange} />
              {fieldErrors.password && <span className="error-text">{fieldErrors.password}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input name="confirm_password" type="password" className={`form-input ${fieldErrors.confirm_password ? 'error' : ''}`} placeholder="Repeat your password" value={form.confirm_password} onChange={handleChange} />
              {fieldErrors.confirm_password && <span className="error-text">{fieldErrors.confirm_password}</span>}
            </div>
            <button type="submit" className="btn btn-primary btn-lg auth-submit-btn" disabled={loading}>
              {loading ? <span className="auth-spinner" /> : 'Create Account'}
            </button>
          </form>
          <div className="auth-form-footer">
            <p>Already have an account? <Link to="/login" className="auth-link">Sign in</Link></p>
          </div>
          <div className="auth-help-banner">
            <span>📖</span>
            <span>Not sure how it works? <a href="/help/parent" target="_blank" rel="noopener noreferrer" className="auth-link">View the Getting Started Guide</a></span>
          </div>
        </div>
      </div>
    </div>
  );
}
